import axios, { AxiosResponse } from 'axios';

export interface ApiResponse<T = any> {
  data: T;
  code: number;
  message?: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ScanRequest {
  qr_code: string;
}

export interface ReservationData {
  id: number;
  code: string;
  location_code: string;
  location_id: number;
  customer_id: number;
  is_inps: number;
  rate_name: string;
  customer_name: string | null;
}

export interface AvailableSlot {
  slot_code: string;
  slot_name: string;
  start_time: string;
  end_time: string;
  discount_type: string;
  base_price: number;
  discount_percentage: number;
  discount_amount: number;
  final_price: number;
  used_count: number;
  meals_limit: number;
}

export interface ScanCheckResponse {
  reservation: ReservationData;
  available_slots: AvailableSlot[];
  slots_source: string;
}

// Legacy interface for compatibility (old single-slot response)
export interface GuestData {
  reservation: ReservationData;
  meal_eligibility: {
    eligible: boolean;
    discount_source: string;
    base_price: number;
    discount_percentage: number;
    discount_amount: number;
    final_price: number;
  };
  slot_info: {
    start_time: string;
    end_time: string;
    current_time: string;
    free_meals_limit: number;
    discounted_meals_limit: number;
  };
  processed_by: string;
  processed_at: string;
}

class ApiService {
  private baseURL: string;
  private apiKey: string;
  private token: string | null = null;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'https://food.campusx.it/api';
    this.apiKey = process.env.REACT_APP_API_KEY || '';

    if (!this.apiKey) {
      console.warn('API_KEY not found in environment variables');
    }

    // Load token from localStorage on initialization
    this.token = localStorage.getItem('authToken');
  }

  private getHeaders(includeAuth: boolean = true) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.apiKey) {
      headers['x-api-key'] = this.apiKey;
    }

    // Add Authorization header for authenticated requests
    if (includeAuth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private handleUnauthorized() {
    // Clear stored authentication data
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');

    // Redirect to login by reloading the page
    console.warn('Session expired. Redirecting to login...');
    window.location.reload();
  }

  private async makeRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    data?: any,
    includeAuth: boolean = true
  ): Promise<ApiResponse<T>> {
    try {
      const config = {
        method,
        url: `${this.baseURL}${endpoint}`,
        headers: this.getHeaders(includeAuth),
        ...(data && { data })
      };

      const response: AxiosResponse<ApiResponse<T>> = await axios(config);
      return response.data;
    } catch (error: any) {
      // Handle 401 Unauthorized responses
      if (error.response?.status === 401 && includeAuth) {
        this.handleUnauthorized();
        throw new Error('Session expired. Please login again.');
      }

      console.error(`API ${method} error for ${endpoint}:`, error);

      // Return API error message if available
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      throw new Error('API request failed. Please try again.');
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  async login(credentials: LoginRequest): Promise<ApiResponse> {
    // Login doesn't require authentication
    const response = await this.makeRequest<any>('POST', '/auth', credentials, false);

    // Store the token if login is successful
    if (response.code === 200 && response.data?.token) {
      this.setToken(response.data.token);
    }

    return response;
  }

  async scanQRCode(scanData: ScanRequest): Promise<ApiResponse<ScanCheckResponse>> {
    // Scan requires authentication
    return await this.makeRequest<ScanCheckResponse>('POST', '/scan-check', scanData, true);
  }

  async consumeMeal(guestData: GuestData, isAccepted: boolean): Promise<ApiResponse> {
    // Consume meal requires authentication and sends the complete guest data with acceptance status
    const payload = {
      guestData,
      is_accepted: isAccepted
    };
    return await this.makeRequest<any>('POST', '/scan-consume', payload, true);
  }

  async consumeMealWithSlot(payload: any): Promise<ApiResponse> {
    // New method for slot-based consumption
    return await this.makeRequest<any>('POST', '/scan-consume', payload, true);
  }

  async getBarLogs(filters: { date_from: string; date_to?: string }): Promise<ApiResponse> {
    // Get bar logs requires authentication
    const queryParams = new URLSearchParams();
    queryParams.append('date_from', filters.date_from);
    if (filters.date_to) {
      queryParams.append('date_to', filters.date_to);
    }

    return await this.makeRequest<any>('GET', `/bar-logs?${queryParams.toString()}`, undefined, true);
  }

  logout() {
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getApiInfo() {
    return {
      baseURL: this.baseURL,
      hasApiKey: !!this.apiKey,
      isAuthenticated: this.isAuthenticated(),
    };
  }
}

export const apiService = new ApiService();
export default apiService;